import { FeedbackDetails } from '../types/flowScenario';

/**
 * FEEDBACK DETAILS NORMALIZATION UTILITY
 *
 * Background:
 * - Originally, feedbackDetails was a single object tracking one follow-up conversation
 * - Now supports multiple follow-up conversations stored as an array
 * - This utility provides backward compatibility while supporting the new structure
 *
 * Migration Strategy:
 * - Old format: feedbackDetails = { appointmentName, currentState, ... }
 * - New format: feedbackDetails = [{ followUpNumber: 1, ... }, { followUpNumber: 2, ... }]
 * - Data is normalized at the provider level (FlowDataProvider)
 * - Components use this utility to safely access the current active follow-up
 *
 * Usage:
 * - Use normalizeFeedbackDetails() to get the current active follow-up and all follow-ups
 * - The "current" follow-up is the first one that's incomplete (not form_submitted)
 * - This allows UI to show the next actionable step without breaking existing flows
 */

/**
 * Normalizes feedbackDetails to handle both old single object format and new array format.
 * Provides backward compatibility while supporting multiple follow-up conversations.
 *
 * @param feedbackDetails - Can be a single FeedbackDetails object (old format) or an array (new format)
 * @returns An object containing the current active feedback (first incomplete follow-up) and all follow-ups
 */
export function normalizeFeedbackDetails(feedbackDetails?: FeedbackDetails | FeedbackDetails[]): {
  currentFeedback?: FeedbackDetails & { followUpNumber: number };
  allFollowUps: (FeedbackDetails & { followUpNumber: number })[];
  hasMultipleFollowUps: boolean;
  allConversationsCompleted: boolean;
  shouldShowFeedbackForm: boolean;
} {
  if (!feedbackDetails) {
    return {
      currentFeedback: undefined,
      allFollowUps: [],
      hasMultipleFollowUps: false,
      allConversationsCompleted: false,
      shouldShowFeedbackForm: false,
    };
  }

  // Convert object format (with numeric keys) to array format
  // Object format: { 0: {...}, 1: {...}, formSubmitted: true, responseId: "..." }
  let normalizedFeedbackDetails: FeedbackDetails | FeedbackDetails[] = feedbackDetails;
  if (!Array.isArray(feedbackDetails) && typeof feedbackDetails === 'object') {
    const metadataKeys = ['formSubmitted', 'responseId'];
    const numericKeys = Object.keys(feedbackDetails)
      .filter((key) => !isNaN(Number(key)) && !metadataKeys.includes(key))
      .sort((a, b) => Number(a) - Number(b));

    if (numericKeys.length > 0) {
      // Convert to array format and override formSubmitted if present at object level
      const objectFormSubmitted = (feedbackDetails as any).formSubmitted;
      normalizedFeedbackDetails = numericKeys.map((key) => ({
        ...(feedbackDetails as any)[key],
        ...(objectFormSubmitted !== undefined && { formSubmitted: objectFormSubmitted }),
      }));
    }
  }

  // Handle array format (new)
  if (Array.isArray(normalizedFeedbackDetails)) {
    const followUps = normalizedFeedbackDetails.map((fd: FeedbackDetails, index: number) => ({
      ...fd,
      followUpNumber: fd.followUpNumber ?? index + 1, // Use array index + 1 if followUpNumber doesn't exist
    }));

    // Check if all conversations are completed (conversation_completed state)
    const allConversationsCompleted = followUps.every(
      (fd: FeedbackDetails & { followUpNumber: number }) =>
        fd.currentState === 'conversation_completed' || fd.currentState === 'form_submitted'
    );

    // Check if we should show the feedback form (all conversations done, form not submitted)
    const shouldShowFeedbackForm =
      allConversationsCompleted &&
      followUps.some(
        (fd: FeedbackDetails & { followUpNumber: number }) =>
          fd.currentState === 'conversation_completed' && !fd.formSubmitted
      );

    // Find the current active follow-up based on priority:
    // 1. First follow-up that is activated and scheduled (show reschedule option)
    // 2. First follow-up that is activated but not scheduled (needs scheduling)
    // 3. If all are conversation_completed, show the first one for feedback form
    let currentFeedback =
      followUps.find(
        (fd: FeedbackDetails & { followUpNumber: number }) =>
          fd.currentState === 'activated' && fd.conversationScheduled
      ) || // Already scheduled - show first
      followUps.find(
        (fd: FeedbackDetails & { followUpNumber: number }) =>
          fd.currentState === 'activated' && !fd.conversationScheduled
      ); // Need to schedule

    // If all conversations are completed, point to the first one for feedback form
    if (!currentFeedback && allConversationsCompleted) {
      currentFeedback =
        followUps.find((fd: FeedbackDetails & { followUpNumber: number }) => !fd.formSubmitted) ||
        followUps[0];
    }

    // Fallback to first if nothing matches
    if (!currentFeedback) {
      currentFeedback = followUps[0];
    }

    return {
      currentFeedback,
      allFollowUps: followUps,
      hasMultipleFollowUps: followUps.length > 1,
      allConversationsCompleted,
      shouldShowFeedbackForm,
    };
  }

  // Handle single object format (old) - convert to array format
  const singleFollowUp = {
    ...(normalizedFeedbackDetails as FeedbackDetails),
    followUpNumber: 1,
  };

  const isConversationCompleted =
    singleFollowUp.currentState === 'conversation_completed' ||
    singleFollowUp.currentState === 'form_submitted';

  const shouldShowFeedbackForm =
    singleFollowUp.currentState === 'conversation_completed' && !singleFollowUp.formSubmitted;

  return {
    currentFeedback: singleFollowUp,
    allFollowUps: [singleFollowUp],
    hasMultipleFollowUps: false,
    allConversationsCompleted: isConversationCompleted,
    shouldShowFeedbackForm,
  };
}

/**
 * Gets a specific follow-up by number
 */
export function getFollowUpByNumber(
  feedbackDetails: FeedbackDetails | FeedbackDetails[] | undefined,
  followUpNumber: number
): (FeedbackDetails & { followUpNumber: number }) | undefined {
  const { allFollowUps } = normalizeFeedbackDetails(feedbackDetails);
  return allFollowUps.find((fd) => fd.followUpNumber === followUpNumber);
}
